import React, {useState} from "react";
import {Box, Dialog, IconButton, Radio, Tooltip, Typography} from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import AddIcon from '@mui/icons-material/Add';
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import {useTranslation} from "react-i18next";
import {PrimaryBigButton, SecondaryBigButton} from "../../components/buttons/mainButton";
import {DefaultEmailTemplate, defaultEmailTemplates} from "./defaultEmailTemplates";
import {TEmailTemplateComponentType} from "../../interfaces/email";
import EmailPreviewFrame from "./EmailPreviewFrame";
import {buildLocalPreviewHtml, ASSET_BASE} from "./useEmailPreview";

const resolveAssetUrl = (path: string): string =>
  /^https?:\/\//.test(path) ? path : `${ASSET_BASE}${path}`;

const getTemplateThumbnail = (template: DefaultEmailTemplate): string | null => {
  if (template.thumbnail) return resolveAssetUrl(template.thumbnail);

  const banner = template.components.find(c => c.type === TEmailTemplateComponentType.BANNER && c.img);
  return banner?.img ? resolveAssetUrl(banner.img) : null;
};


const BLANK_ID = '__blank__';

const iconMap: Record<string, React.ElementType> = {
  PaidIcon,
  DoDisturbIcon,
  EqualizerIcon,
  AddIcon,
  PlayCircleFilledWhiteIcon,
};

type EmailTemplateSelectorProps = {
  onSelectCustom: () => void;
  onSelectDefault: (template: DefaultEmailTemplate) => void;
  onCancel: () => void;
  addError: (error: string) => void;
};

type TemplateCardProps = {
  name: string;
  subject?: string;
  icon: React.ElementType;
  selected: boolean;
  blank?: boolean;
  thumbnail?: string | null;
  onSelect: () => void;
  onPreview?: () => void;
};

function TemplateCard({name, subject, icon: IconComponent, selected, blank, thumbnail, onSelect, onPreview}: TemplateCardProps): JSX.Element {
  const {t} = useTranslation();
  const [imgError, setImgError] = useState(false);
  const showThumbnail = Boolean(thumbnail) && !imgError;

  return (
    <Box
      onClick={onSelect}
      sx={{
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          height: 150,
          mx: 1,
          mt: 1,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: blank ? 'ornament.light' : 'ornament.main',
          ...(blank ? {border: '2px dashed', borderColor: 'ornament.dark'} : {}),
        }}
      >
        <Radio
          checked={selected}
          onChange={onSelect}
          sx={{
            position: 'absolute', top: 8, right: 8, zIndex: 1,
            backgroundColor: 'rgba(255,255,255,0.85)',
            '&:hover': {backgroundColor: 'rgba(255,255,255,0.95)'},
          }}
        />
        {showThumbnail
          ? <Box
            component="img"
            src={thumbnail!}
            alt=""
            onError={(): void => setImgError(true)}
            sx={{width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block'}}
          />
          : <IconComponent sx={{fontSize: 24, color: blank ? 'ornament.dark' : 'neutral.dark'}} />
        }
      </Box>
      <Box sx={{p: 2, display: 'flex', alignItems: 'center', gap: 1}}>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography variant="subtitle2" sx={{fontWeight: 'bold', color: 'neutral.dark'}}>
            {name}
          </Typography>
          {subject !== undefined && (
            <Typography variant="caption" color="neutral.main" sx={{display: 'block'}}>
              {subject}
            </Typography>
          )}
        </Box>
        {onPreview && (
          <Tooltip title={t("email_templates.preview.preview")} arrow>
            <IconButton
              size="small"
              sx={{p: 1}}
              onClick={(e): void => { e.stopPropagation(); onPreview(); }}
            >
              <VisibilityIcon sx={{fontSize: 18, color: 'neutral.main'}} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export default function EmailTemplateSelector({onSelectCustom, onSelectDefault, onCancel}: EmailTemplateSelectorProps): JSX.Element {
  const {t} = useTranslation();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<DefaultEmailTemplate | null>(null);

  const selectedTemplate = defaultEmailTemplates.find(dt => dt.id === selectedTemplateId) ?? null;
  const isBlankSelected = selectedTemplateId === BLANK_ID;

  const handleConfirm = (): void => {
    if (isBlankSelected) {
      onSelectCustom();
    } else if (selectedTemplate) {
      onSelectDefault(selectedTemplate);
    }
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'ornament.light'}}>
      <Box sx={{flex: 1, overflowY: 'auto', p: 4, backgroundColor: 'ornament.main'}}>
        <Typography variant="body1" sx={{fontWeight: 'bold', color: 'neutral.main', mb: 2}}>
          {t("email_templates.selector.title")}
        </Typography>

        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2}}>
          <TemplateCard
            name={t("email_templates.default.empty_template")}
            subject={t("email_templates.default.empty_template_description")}
            icon={AddIcon}
            blank
            selected={isBlankSelected}
            onSelect={(): void => setSelectedTemplateId(BLANK_ID)}
          />

          {defaultEmailTemplates.map(dt => (
            <TemplateCard
              key={dt.id}
              name={dt.name}
              subject={dt.subject}
              icon={iconMap[dt.icon] ?? PaidIcon}
              thumbnail={getTemplateThumbnail(dt)}
              selected={selectedTemplateId === dt.id}
              onSelect={(): void => setSelectedTemplateId(dt.id)}
              onPreview={(): void => setPreviewTemplate(dt)}
            />
          ))}

        </Box>
      </Box>

      <Box sx={{display: 'flex', gap: 2, p: 3, borderTop: '1px solid', borderColor: 'ornament.dark', backgroundColor: 'white'}}>
        <SecondaryBigButton label={t("navigation.cancel")} action={onCancel} sx={{flex: 1}} />
        <PrimaryBigButton
          label={isBlankSelected ? t("email_templates.selector.continue") : t("email_templates.selector.use_template")}
          action={handleConfirm}
          sx={{flex: 1}}
          disabled={selectedTemplateId === null}
        />
      </Box>
      <Dialog
        open={previewTemplate !== null}
        onClose={(): void => setPreviewTemplate(null)}
        maxWidth="sm"
        fullWidth
        sx={{"& .MuiDialog-container": {height: "100%"}}}
      >
        {previewTemplate && (
          <EmailPreviewFrame
            html={buildLocalPreviewHtml(previewTemplate.components)}
            status="ready"
            sender={previewTemplate.mailSender || ""}
            subject={previewTemplate.subject || ""}
            title={previewTemplate.name}
            minHeight={300}
            bordered={false}
            headerAction={
              <IconButton size="small" sx={{p: 0}} onClick={(): void => setPreviewTemplate(null)}>
                <ClearIcon sx={{width: 20, height: 20, color: "neutral.main"}} />
              </IconButton>
            }
          />
        )}
      </Dialog>
    </Box>
  );
}
